import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../services/firebase';
import { 
  onAuthStateChanged, 
  User,
  signInWithPopup,
  signOut,
  GoogleAuthProvider
} from 'firebase/auth';
import { googleProvider } from '../services/firebase';

// Define the user profile type (matches Firestore structure)
interface UserProfile {
  email: string;
  displayName: string | null;
  roles: string[];
  createdAt: string;
  lastLogin: string;
}

// Define the context type
interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
  getIdToken: () => Promise<string | null>;
}

// Create the context with default values
const AuthContext = createContext<AuthContextType>({
  user: null,
  userProfile: null,
  loading: true,
  error: null,
  signInWithGoogle: async () => {},
  logout: async () => {},
  getIdToken: async () => null,
});

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Listen for authentication state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      if (user) {
        try {
          // Fetch user profile from backend
          const token = await user.getIdToken();
          const backendUrl = window.location.hostname === 'localhost' 
            ? 'http://localhost:8080'
            : 'https://voice-coach-backend-406125116283.us-east1.run.app';
            
          const response = await fetch(`${backendUrl}/api/user/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const profile = await response.json();
            
            // Check for development role override via URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const roleOverride = urlParams.get('roles');
            if (roleOverride && window.location.hostname === 'localhost') {
              const overrideRoles = roleOverride.split(',');
              console.log('🔧 Development: Overriding roles with URL param:', overrideRoles);
              profile.roles = overrideRoles;
            }
            
            setUserProfile(profile);
          } else {
            // Profile endpoint might not exist yet, log the error
            console.warn('❌ User profile endpoint failed:', response.status, response.statusText);
            const errorText = await response.text();
            console.warn('Response:', errorText);
            
            // Check for development role override via URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const roleOverride = urlParams.get('roles');
            
            let defaultRoles = ['learner'];
            
            if (roleOverride && window.location.hostname === 'localhost') {
              // Development: Allow role override via URL parameter
              defaultRoles = roleOverride.split(',');
              console.log('🔧 Development: Using URL role override:', defaultRoles);
            } else {
              // For development - check if user should have admin/coach access
              // In production, this should be handled by proper backend user management
              const adminEmails: string[] = [
                // Add known admin emails here for development
                'rashiverma@gmail.com',
                'deepankar.sharma@gmail.com'
              ];
              const coachEmails: string[] = [
                // Add known coach emails here for development  
              ];
              
              if (user.email && adminEmails.includes(user.email)) {
                defaultRoles = ['learner', 'coach', 'admin'];
              } else if (user.email && coachEmails.includes(user.email)) {
                defaultRoles = ['learner', 'coach'];
              }
            }
            
            // Set a basic profile based on auth data
            setUserProfile({
              email: user.email || '',
              displayName: user.displayName,
              roles: defaultRoles,
              createdAt: new Date().toISOString(),
              lastLogin: new Date().toISOString()
            });
            console.log('🔧 Set fallback profile with roles:', defaultRoles);
          }
        } catch (err) {
          console.error('Error fetching user profile:', err);
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign in with Google
  const signInWithGoogle = async () => {
    setError(null);
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Successfully signed in:', result.user.email);
    } catch (error: any) {
      console.error('Authentication error:', error);
      setError(error.message || 'Failed to sign in');
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
      setUser(null);
      setUserProfile(null);
      console.log('Successfully signed out');
    } catch (error: any) {
      console.error('Logout error:', error);
      setError(error.message || 'Failed to sign out');
      throw error;
    }
  };

  // Get current user's ID token
  const getIdToken = async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  };

  const value: AuthContextType = {
    user,
    userProfile,
    loading,
    error,
    signInWithGoogle,
    logout,
    getIdToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper hook to check if user has a specific role
export const useRole = (role: string): boolean => {
  const { userProfile } = useAuth();
  return userProfile?.roles?.includes(role) || false;
};

// Helper hook to check if user is admin
export const useIsAdmin = (): boolean => {
  return useRole('admin');
};

// Helper hook to check if user is coach
export const useIsCoach = (): boolean => {
  return useRole('coach');
};